// Backend datetimes are displayed exactly as received — same timezone, no conversion.
// Fields are read straight from the string ("YYYY-MM-DD", "YYYY-MM-DD HH:MM:SS",
// ISO "YYYY-MM-DDTHH:MM:SS.uuuuuuZ" or time-only "HH:MM:SS") instead of going through Date.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type DateParts = { y: number; m: number; d: number };
type TimeParts = { h: number; i: number };

const pad = (n: number): string => String(n).padStart(2, '0');

const parseDate = (s: string): DateParts | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
    if (!match) return null;
    const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
    return m >= 1 && m <= 12 ? { y, m, d } : null;
};

const parseTime = (s: string): TimeParts | null => {
    const match = /(?:^|[ T])(\d{2}):(\d{2})/.exec(s.trim());
    return match ? { h: Number(match[1]), i: Number(match[2]) } : null;
};

const formatDate = ({ y, m, d }: DateParts): string => `${pad(d)} ${MONTHS[m - 1]} ${y}`;

// h:i A
const formatTime = ({ h, i }: TimeParts): string => `${pad(h % 12 || 12)}:${pad(i)} ${h < 12 ? 'AM' : 'PM'}`;

// Calendar math only — UTC is used as a neutral zone so the day never shifts
const weekdayOf = ({ y, m, d }: DateParts): string => WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];

/** Format time only, as received — e.g. "09:05 PM" */
export const fmtTime = (s?: string | null): string => {
    if (!s) return '—';
    const t = parseTime(s);
    return t ? formatTime(t) : s;
};

/** Format date only, as received — e.g. "15 Jan 2024" */
export const fmtDate = (s?: string | null): string => {
    if (!s) return '—';
    const p = parseDate(s);
    return p ? formatDate(p) : s;
};

/** Format full datetime, as received — e.g. "15 Jan 2024, 09:05 PM" */
export const fmtDateTime = (s?: string | null): string => {
    if (!s) return '—';
    const p = parseDate(s);
    if (!p) return s;
    const t = parseTime(s);
    return t ? `${formatDate(p)}, ${formatTime(t)}` : formatDate(p);
};

/** Format "YYYY-MM" string as "January 2024" */
export const fmtMonthYear = (ym?: string | null): string => {
    if (!ym) return '—';
    const p = parseDate(ym + '-01');
    return p ? `${MONTHS_LONG[p.m - 1]} ${p.y}` : ym;
};

/** Format a JS Date object (local calendar fields) — e.g. "15 Jan 2024" */
export const fmtDateObj = (d: Date): string =>
    formatDate({ y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() });

/** Format date with weekday, as received — e.g. "Mon, 15 Jan 2024" */
export const fmtDateWithDay = (s?: string | null): string => {
    if (!s) return '—';
    const p = parseDate(s);
    return p ? `${weekdayOf(p)}, ${formatDate(p)}` : s;
};

/** Format date as weekday only — e.g. "Mon" */
export const fmtWeekday = (s?: string | null): string => {
    if (!s) return '';
    const p = parseDate(s);
    return p ? weekdayOf(p) : '';
};

/** Format date as month name only — e.g. "January" */
export const fmtMonth = (s?: string | null): string => {
    if (!s) return '';
    const p = parseDate(s);
    return p ? MONTHS_LONG[p.m - 1] : '';
};
